/**
 * routeAnchorGizmo — the root points and heading ticks of a planned route,
 * drawn as one dot per `root_xz` entry the kimodo request carries plus a short
 * line showing the facing asked for there.
 *
 * Shared by the Generate-Body-Animation overlay and the Walk-here preview
 * because they are showing the SAME thing: a route plus the constraint built
 * from it. Two copies of this would drift, and a preview that has drifted from
 * the request is worse than no preview — it would show a correct request as
 * broken, or hide a broken one.
 *
 * The dots ARE the request. They are deliberately not subsampled: a thinned
 * picture can hide exactly the frames where the heading and the path disagree,
 * which is the thing an author is looking for. Drawn faintly instead — every
 * anchor is there to be read when looked for, and recedes when it is not.
 */
import * as THREE from 'three'
import { WAYPOINT_COLORS } from '../lib/waypointColors'
import { waypointPickerStore } from '../lib/waypointPickerStore'

/**
 * OFF by default. These are a diagnostic — every root point of the request and
 * the facing asked for at each — and on a normal route there are dozens, which
 * is exactly what makes them useful when debugging the constraint and clutter
 * the rest of the time.
 *
 * Turn them on from the console with `__SHOW_ROUTE_ANCHORS__()`, off with
 * `__SHOW_ROUTE_ANCHORS__(false)`. Deliberately a console seam and not a panel
 * toggle: nobody authoring a walk needs it, and a checkbox for it would be one
 * more thing in a panel that already earns its space.
 */
export function routeAnchorsVisible() {
  return typeof window !== 'undefined' && window.__ROUTE_ANCHORS__ === true
}

if (typeof window !== 'undefined') {
  window.__SHOW_ROUTE_ANCHORS__ = (on = true) => {
    window.__ROUTE_ANCHORS__ = !!on
    // Re-emit the picker's route so the wizard overlay rebuilds now rather than
    // on the next edit. A shallow clone, because the overlay skips a rebuild
    // when the route is the same object it drew last. Walk-here rebuilds on its
    // next click, which is soon enough — it has no standing route to redraw.
    try {
      const r = waypointPickerStore.getState().route
      if (r) waypointPickerStore.setRoute({ ...r })
    } catch { /* store not mounted; the next plan picks the flag up */ }
    return window.__ROUTE_ANCHORS__
  }
}

/**
 * @param {Array<{x:number, z:number, fx:number, fz:number}>} headings
 *   from routeHeadingPreview: world position of each anchor + the FACING
 *   requested there (already un-swapped out of kimodo's hip-line form).
 * @param {number} floorY  world floor height to draw at
 * @returns {THREE.Object3D[]} objects to add to the overlay group ([] if none)
 */
export function buildAnchorGizmo(headings, floorY) {
  if (!routeAnchorsVisible() || !headings?.length) return []
  const y = floorY + 0.06

  // Tick length from the spacing between anchors: at stride 3 they sit ~13 cm
  // apart, and a fixed length long enough to read on a sparse route merges into
  // a solid band on a dense one.
  let spacing = 0
  for (let i = 1; i < headings.length; i++) {
    spacing += Math.hypot(headings[i].x - headings[i - 1].x, headings[i].z - headings[i - 1].z)
  }
  spacing = headings.length > 1 ? spacing / (headings.length - 1) : 0.3
  // Shorter than the gap between anchors, so the ticks read as marks along the
  // route rather than a hatched band lying over it.
  const len = Math.min(0.22, Math.max(0.06, spacing * 0.8))

  const pts = new Float32Array(headings.length * 3)
  const seg = new Float32Array(headings.length * 6)
  for (let i = 0; i < headings.length; i++) {
    const h = headings[i]
    pts[i * 3] = h.x; pts[i * 3 + 1] = y; pts[i * 3 + 2] = h.z
    seg[i * 6] = h.x; seg[i * 6 + 1] = y; seg[i * 6 + 2] = h.z
    seg[i * 6 + 3] = h.x + h.fx * len
    seg[i * 6 + 4] = y
    seg[i * 6 + 5] = h.z + h.fz * len
  }

  // Both transparent + depthTest off, like the route line beside them: an opaque
  // draw lands before the gaussian-splat pass and the splats paint over it
  // whatever the renderOrder.
  const dots = new THREE.Points(
    new THREE.BufferGeometry().setAttribute('position', new THREE.BufferAttribute(pts, 3)),
    new THREE.PointsMaterial({
      color: WAYPOINT_COLORS.anchor.num,
      // Screen-constant: an anchor is a fact about the request, not a thing in
      // the world that should shrink with distance.
      size: 3, sizeAttenuation: false,
      // This is diagnostic overlay, not part of the scene: it sits UNDER the
      // route line and the waypoint markers in visual weight, so it can be read
      // when looked for and ignored when not.
      opacity: 0.45,
      depthTest: false, depthWrite: false, transparent: true,
    }),
  )
  dots.renderOrder = 9999

  const ticks = new THREE.LineSegments(
    new THREE.BufferGeometry().setAttribute('position', new THREE.BufferAttribute(seg, 3)),
    new THREE.LineBasicMaterial({
      color: WAYPOINT_COLORS.heading.num,
      opacity: 0.4,
      depthTest: false, depthWrite: false, transparent: true,
    }),
  )
  ticks.renderOrder = 9999

  return [dots, ticks]
}
